/**
 * A relation's operator — "= 𝔐", "cI⁻³⁹⁵", "La: cf 𝔐" — as the API defines it
 * (`HexaplaApi.Lemmas.Relation`, served at `GET /api/relations/types`): the
 * operators with their signs, and which owner takes which. The portal keeps no
 * copy of either; it adds only how the line lays them out.
 */

import Client from '@/hexapla-core/client'
import { ApparatusRelation } from '@/hexapla-core/domains/resources'

/** What owns a relation: a citation ("C (= 257)"), an attribution's citation, a whole reading, or a stretch of one. */
export type RelationOwner = 'citation' | 'attribution' | 'reading' | 'segment'

/** An operator: its stored type, its printed sign and its name. */
export interface RelationType {
  type: string
  sign: string
  name: string
}

/** Whether an owner's relation needs a type, and which it takes, in the order offered. */
export interface OwnerRule {
  required: boolean
  types: string[]
}

/** The vocabulary as the API serves it. */
export interface RelationVocabularyData {
  types: RelationType[]
  owners: Record<RelationOwner, OwnerRule>
}

/** A reading's relations as the Edition prints them, after its witnesses. */
export interface ReadingRelations {
  /** Printed in place, in parentheses when the reading groups them: "137 (= 260)". */
  marks: ApparatusRelation[]
  /** Printed last, after `conferLead`: "La: cf 𝔐", "137 (= 260): cf 𝔐". */
  confer: ApparatusRelation[]
}

/** Consecutive relations of one type, printed under a single sign. */
export interface RelationGroup {
  relType: string
  sign: string
  /** Exclusions and inclusions print as a superscript after the witness. */
  superscript: boolean
  relations: ApparatusRelation[]
}

// The layout the portal adds: which signs stand superscript ("cI⁻¹³⁸"), and
// the operator a reading prints last, after a colon ("La: cf 𝔐").
const SUPERSCRIPT = ['exclude', 'include']
const CONFER = 'confer'

export class RelationVocabulary {
  private readonly types: Map<string, RelationType>

  constructor(private readonly data: RelationVocabularyData) {
    this.types = new Map(data.types.map((t) => [t.type, t]))
  }

  static async load(apiUrl: string): Promise<RelationVocabulary> {
    return new RelationVocabulary(await Client.get<RelationVocabularyData>(`${apiUrl}/api/relations/types`))
  }

  /** The operators an owner takes, in the order an editor offers them. */
  offers(owner: RelationOwner): RelationType[] {
    return this.rule(owner).types.flatMap((type) => this.types.get(type) ?? [])
  }

  /** Whether an owner's relation needs a type: a citation's does; a reading's may print its target bare, "(575)". */
  needsType(owner: RelationOwner): boolean {
    return this.rule(owner).required
  }

  /** The type after choosing `chosen`: choosing it again clears it, unless the owner needs one. */
  next(owner: RelationOwner, current: string | null, chosen: string): string | null {
    return current === chosen && !this.needsType(owner) ? null : chosen
  }

  /** Whether a target may be added yet: an owner that needs a type needs it chosen first. */
  canAdd(owner: RelationOwner, relType: string | null): boolean {
    return !this.needsType(owner) || !!relType
  }

  /**
   * An owner's relations once `chosen` is its type: the marks take it, the way
   * "(= 257 788-250 3005)" shares one sign. A reference to compare stands
   * apart — choosing cf retypes nothing, and no other choice retypes a cf — so
   * "137 (= 260): cf 𝔐" keeps its "= 260".
   */
  retype(relations: ApparatusRelation[], chosen: string | null): ApparatusRelation[] {
    if (chosen === CONFER) return relations
    return relations.map((r) => (r.rel_type === CONFER ? r : { ...r, rel_type: chosen }))
  }

  /** The sign of a type; empty for none or an unknown one. */
  sign(relType: string | null | undefined): string {
    return (relType && this.types.get(relType)?.sign) || ''
  }

  /** A relation's sign as printed in the line, set apart from its target: "= 𝔐", "cf 𝔐". */
  lead(relType: string | null | undefined): string {
    const sign = this.sign(relType)
    return sign ? `${sign} ` : ''
  }

  /** What opens a reading's references to compare: the colon closing its witnesses, then the sign. */
  get conferLead(): string {
    return `: ${this.lead(CONFER)}`
  }

  /** A reading's relations split into those printed in place and those printed last. */
  split(relations: ApparatusRelation[] | null | undefined): ReadingRelations {
    return {
      marks: (relations || []).filter((r) => r.rel_type !== CONFER),
      confer: (relations || []).filter((r) => r.rel_type === CONFER),
    }
  }

  /**
   * A witness's relations as the Edition prints them: consecutive relations of
   * the same type share one sign — "cI⁻¹³⁸ ¹³⁹", not "cI⁻¹³⁸⁻¹³⁹". Order is kept.
   */
  groups(relations: ApparatusRelation[] | null | undefined): RelationGroup[] {
    return (relations || []).reduce((groups: RelationGroup[], relation) => {
      const relType = relation.rel_type || ''
      const last = groups[groups.length - 1]
      if (last && last.relType === relType) {
        last.relations.push(relation)
      } else {
        groups.push({
          relType,
          sign: this.sign(relType),
          superscript: SUPERSCRIPT.includes(relType),
          relations: [relation],
        })
      }
      return groups
    }, [])
  }

  private rule(owner: RelationOwner): OwnerRule {
    return this.data.owners[owner] ?? { required: false, types: [] }
  }
}
